import { supabase } from '../lib/supabase';
import { 
  sendStatusUpdateNotifications,
  getUserEmail,
  getCompanyEmail 
} from './emailNotificationService';
import { triggerApplicationStatusNotification } from './notificationTriggers';
import dataConsistencyService from './dataConsistencyService';
import { trackApiCall } from './performanceMonitoringService';
import { createQuery } from '../utils/databaseOptimization';
import { queryCache } from '../utils/cacheStrategies';

/**
 * Applications Service
 * Handles all application-related operations for the company dashboard
 */

/**
 * Fetch all applications for a company's jobs with filtering options
 * @param {string} companyId - The company ID
 * @param {Object} filters - Filter options
 * @param {string} filters.jobId - Filter by specific job
 * @param {string} filters.status - Filter by application status
 * @param {string} filters.dateFrom - Filter by date range start
 * @param {string} filters.dateTo - Filter by date range end
 * @param {string} filters.sortBy - Sort field (applied_at, updated_at, status)
 * @param {string} filters.sortOrder - Sort order (asc, desc)
 * @returns {Promise<Array>} Array of applications with candidate and job details
 */
export const fetchCompanyApplications = async (companyId, filters = {}) => {
  try {
    const {
      jobId,
      status,
      dateFrom,
      dateTo,
      sortBy = 'applied_at',
      sortOrder = 'desc'
    } = filters;

    // Build query to get applications for company's jobs
    let query = supabase?.from('job_applications')?.select(`
        id,
        job_id,
        applicant_id,
        resume_url,
        cover_letter,
        status,
        applied_at,
        updated_at,
        jobs!inner (
          id,
          title,
          company_id,
          employment_type,
          location_id,
          company_locations (
            city,
            state,
            country
          )
        ),
        user_profiles!job_applications_applicant_id_fkey (
          id,
          email,
          full_name,
          avatar_url
        )
      `)?.eq('jobs.company_id', companyId);

    // Apply filters
    if (jobId) {
      query = query?.eq('job_id', jobId);
    }

    if (status) {
      query = query?.eq('status', status);
    }

    if (dateFrom) {
      query = query?.gte('applied_at', dateFrom);
    }

    if (dateTo) {
      query = query?.lte('applied_at', dateTo);
    }

    // Apply sorting
    query = query?.order(sortBy, { ascending: sortOrder === 'asc' });

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching applications:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch company applications:', error);
    throw error;
  }
};

/**
 * Fetch application details by ID with full candidate and job information
 * @param {string} applicationId - The application ID
 * @returns {Promise<Object>} Application details with candidate and job info
 */
export const fetchApplicationDetails = async (applicationId) => {
  try {
    const { data, error } = await supabase?.from('job_applications')?.select(`
        *,
        jobs (
          id,
          title,
          description,
          requirements,
          employment_type,
          experience_level,
          salary_min,
          salary_max,
          salary_type,
          company_id,
          companies (
            id,
            name,
            logo_url
          ),
          company_locations (
            address,
            city,
            state,
            country
          )
        ),
        user_profiles!job_applications_applicant_id_fkey (
          id,
          email,
          full_name,
          avatar_url,
          phone,
          job_seeker_profiles (
            resume_url,
            skills,
            experience_years,
            bio,
            location
          )
        )
      `)?.eq('id', applicationId)?.single();

    if (error) {
      console.error('Error fetching application details:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Failed to fetch application details:', error);
    throw error;
  }
};

// Optimized version of getApplications with caching and performance tracking
export const getApplications = async (userId, filters = {}) => {
  return trackApiCall('getApplications', async () => {
    try {
      // Use optimized query builder with caching
      let query = createQuery('job_applications')?.select(`
          *,
          job:jobs (
            id,
            title,
            company_id,
            location,
            salary_range,
            job_type,
            company:company_profiles (
              id,
              company_name,
              logo_url
            )
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false })?.cacheTTL(60000); // 1 minute cache

      // Apply filters
      if (filters?.status) {
        query?.eq('status', filters?.status);
      }

      const { data, error } = await query?.execute();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching applications:', error);
      return { data: null, error };
    }
  });
};

// Optimized getApplicationById with caching
export const getApplicationById = async (applicationId) => {
  return trackApiCall('getApplicationById', async () => {
    try {
      let query = createQuery('job_applications')?.select(`
          *,
          job:jobs (
            *,
            company:company_profiles (*)
          ),
          history:application_status_history (
            *,
            changed_by_user:user_profiles (
              id,
              full_name,
              avatar_url
            )
          )
        `)?.eq('id', applicationId)?.cacheTTL(30000); // 30 seconds cache

      const { data, error } = await query?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching application:', error);
      return { data: null, error };
    }
  });
};

/**
 * Update application status
 * @param {string} applicationId - The application ID
 * @param {string} newStatus - The new status (pending, reviewed, interview, offer, accepted, rejected)
 * @param {string} userId - The user ID performing the update
 * @returns {Promise<Object>} Updated application
 */
export const updateApplicationStatus = async (applicationId, newStatus, userId) => {
  const result = await trackApiCall('updateApplicationStatus', async () => {
    try {
      const { data, error } = await supabase?.from('job_applications')?.update({ 
        status: newStatus,
        updated_at: new Date()?.toISOString()
      })?.eq('id', applicationId)?.eq('user_id', userId)?.select()?.single();

      if (error) throw error;

      // Cascade update to maintain consistency
      await dataConsistencyService?.cascadeUpdate('job_application', applicationId, {
        status: newStatus,
        user_id: userId
      });

      return { data, error: null };
    } catch (error) {
      console.error('Error updating application status:', error);
      return { data: null, error };
    }
  });

  return result;
};

/**
 * Enhanced update function with notes support
 * @param {string} applicationId - The application ID
 * @param {string} newStatus - The new status (pending, reviewed, interview, offer, accepted, rejected)
 * @param {string} notes - Optional notes to add to status history
 * @returns {Promise<Object>} Updated application
 */
export const updateApplicationStatusWithNotes = async (applicationId, newStatus, notes = null) => {
  try {
    // Update the application status
    const { data, error } = await supabase?.from('job_applications')?.update({ 
        status: newStatus,
        updated_at: new Date()?.toISOString()
      })?.eq('id', applicationId)?.select()?.single();

    if (error) throw error;

    // If notes are provided, update the most recent status history entry
    if (notes && notes?.trim()) {
      const { error: notesError } = await supabase?.from('application_status_history')?.update({ notes: notes?.trim() })?.eq('application_id', applicationId)?.eq('new_status', newStatus)?.order('created_at', { ascending: false })?.limit(1);

      if (notesError) {
        console.warn('Failed to add notes to status history:', notesError);
      }
    }

    return data;
  } catch (error) {
    console.error('Error updating application status with notes:', error);
    throw error;
  }
};

/**
 * Bulk update application statuses
 * @param {Array<string>} applicationIds - Array of application IDs
 * @param {string} newStatus - The new status to apply to all applications
 * @returns {Promise<Array>} Updated applications
 */
export const bulkUpdateApplicationStatus = async (applicationIds, newStatus) => {
  try {
    const { data, error } = await supabase?.from('job_applications')?.update({
        status: newStatus,
        updated_at: new Date()?.toISOString()
      })?.in('id', applicationIds)?.select();

    if (error) {
      console.error('Error bulk updating applications:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Failed to bulk update applications:', error);
    throw error;
  }
};

/**
 * Get application status history for an application
 * @param {string} applicationId - The application ID
 * @returns {Promise<Array>} Array of status history entries
 */
export const getApplicationStatusHistory = async (applicationId) => {
  try {
    const { data, error } = await supabase?.from('application_status_history')?.select(`
        *,
        changed_by_user:user_profiles!changed_by (
          id,
          full_name,
          email,
          avatar_url
        )
      `)?.eq('application_id', applicationId)?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching status history:', error);
    throw error;
  }
};

/**
 * Get application statistics for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<Object>} Application statistics
 */
export const getApplicationStatistics = async (companyId) => {
  try {
    // Get all applications for company's jobs
    const { data, error } = await supabase?.from('job_applications')?.select(`
        id,
        status,
        applied_at,
        jobs!inner (
          company_id
        )
      `)?.eq('jobs.company_id', companyId);

    if (error) {
      console.error('Error fetching application statistics:', error);
      throw error;
    }

    // Calculate statistics
    const total = data?.length || 0;
    const byStatus = {
      pending: 0,
      reviewed: 0,
      interview: 0,
      offer: 0,
      accepted: 0,
      rejected: 0
    };

    data?.forEach(app => {
      if (byStatus?.hasOwnProperty(app?.status)) {
        byStatus[app.status]++;
      }
    });

    // Calculate applications this week
    const oneWeekAgo = new Date();
    oneWeekAgo?.setDate(oneWeekAgo?.getDate() - 7);
    const thisWeek = data?.filter(app => new Date(app.applied_at) >= oneWeekAgo)?.length || 0;

    return {
      total,
      byStatus,
      thisWeek
    };
  } catch (error) {
    console.error('Failed to get application statistics:', error);
    throw error;
  }
};

/**
 * Download resume from storage
 * @param {string} resumeUrl - The resume storage path
 * @returns {Promise<string>} Signed URL for resume download
 */
export const getResumeDownloadUrl = async (resumeUrl) => {
  try {
    if (!resumeUrl) {
      throw new Error('Resume URL is required');
    }

    // Extract the file path from the full URL if needed
    const filePath = resumeUrl?.includes('/')
      ? resumeUrl?.split('/')?.slice(-2)?.join('/')
      : resumeUrl;

    const { data, error } = await supabase?.storage?.from('resumes')?.createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) {
      console.error('Error creating signed URL:', error);
      throw error;
    }

    return data?.signedUrl;
  } catch (error) {
    console.error('Failed to get resume download URL:', error);
    throw error;
  }
};

/**
 * Get unique jobs for filtering
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of jobs with application counts
 */
export const getJobsWithApplicationCounts = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('jobs')?.select(`
        id,
        title,
        status,
        posted_at,
        job_applications (
          id
        )
      `)?.eq('company_id', companyId)?.order('posted_at', { ascending: false });

    if (error) {
      console.error('Error fetching jobs with counts:', error);
      throw error;
    }

    // Format data with application counts
    return (data || [])?.map(job => ({
      id: job?.id,
      title: job?.title,
      status: job?.status,
      postedAt: job?.posted_at,
      applicationCount: job?.job_applications?.length || 0
    }));
  } catch (error) {
    console.error('Failed to fetch jobs with application counts:', error);
    throw error;
  }
};

/**
 * Fetch all applications for a job seeker (by applicant_id)
 * @param {string} applicantId - The applicant user ID
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Filter by application status
 * @param {string} filters.sortBy - Sort field (applied_at, updated_at, status)
 * @param {string} filters.sortOrder - Sort order (asc, desc)
 * @returns {Promise<Array>} Array of applications with job and company details
 */
export const fetchJobSeekerApplications = async (applicantId, filters = {}) => {
  try {
    const {
      status,
      sortBy = 'applied_at',
      sortOrder = 'desc'
    } = filters;

    // Build query to get applications for the job seeker
    let query = supabase?.from('job_applications')?.select(`
        id,
        job_id,
        applicant_id,
        resume_url,
        cover_letter,
        status,
        applied_at,
        updated_at,
        jobs!inner (
          id,
          title,
          description,
          employment_type,
          experience_level,
          salary_min,
          salary_max,
          salary_type,
          location_id,
          company_id,
          companies (
            id,
            name,
            logo_url,
            industry
          ),
          company_locations (
            id,
            city,
            state,
            country
          )
        )
      `)?.eq('applicant_id', applicantId);

    // Apply status filter if provided
    if (status && status !== 'all') {
      query = query?.eq('status', status);
    }

    // Apply sorting
    query = query?.order(sortBy, { ascending: sortOrder === 'asc' });

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching job seeker applications:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch job seeker applications:', error);
    throw error;
  }
};

/**
 * Get application statistics for a job seeker
 * @param {string} applicantId - The applicant user ID
 * @returns {Promise<Object>} Application statistics
 */
export const getJobSeekerApplicationStatistics = async (applicantId) => {
  try {
    const { data, error } = await supabase?.from('job_applications')?.select('id, status, applied_at')?.eq('applicant_id', applicantId);

    if (error) {
      console.error('Error fetching job seeker statistics:', error);
      throw error;
    }

    // Calculate statistics
    const total = data?.length || 0;
    const byStatus = {
      pending: 0,
      reviewed: 0,
      interview: 0,
      offer: 0,
      accepted: 0,
      rejected: 0
    };

    data?.forEach(app => {
      if (byStatus?.hasOwnProperty(app?.status)) {
        byStatus[app?.status]++;
      }
    });

    // Calculate applications this week
    const oneWeekAgo = new Date();
    oneWeekAgo?.setDate(oneWeekAgo?.getDate() - 7);
    const thisWeek = data?.filter(app => new Date(app?.applied_at) >= oneWeekAgo)?.length || 0;

    return {
      total,
      byStatus,
      thisWeek,
      active: byStatus?.pending + byStatus?.reviewed + byStatus?.interview + byStatus?.offer
    };
  } catch (error) {
    console.error('Failed to get job seeker application statistics:', error);
    throw error;
  }
};

/**
 * Updates application status with email notifications
 * @param {string} applicationId - Application ID
 * @param {string} newStatus - New status
 * @param {string} notes - Optional notes from company
 * @param {string} updatedBy - User making the update
 * @returns {Promise<Object>} - Update result
 */
export const updateApplicationStatusWithNotification = async (applicationId, newStatus, notes = null, updatedBy = null) => {
  try {
    // Fetch current application data before update
    const { data: currentApp, error: fetchError } = await supabase?.from('job_applications')?.select(`
        *,
        jobs:job_id (
          id,
          title,
          companies:company_id (
            id,
            name
          )
        ),
        user_profiles:applicant_id (
          id,
          full_name,
          email
        )
      `)?.eq('id', applicationId)?.single();
    
    if (fetchError) throw fetchError;
    
    const oldStatus = currentApp?.status;
    
    // Update status in database
    const { data: updatedApp, error: updateError } = await supabase?.from('job_applications')?.update({
        status: newStatus,
        updated_at: new Date()?.toISOString()
      })?.eq('id', applicationId)?.select()?.single();
    
    if (updateError) throw updateError;
    
    // Trigger in-app notification
    try {
      await triggerApplicationStatusNotification(applicationId, newStatus, {
        jobTitle: currentApp?.jobs?.title,
        companyName: currentApp?.jobs?.companies?.name,
        applicantId: currentApp?.applicant_id
      });
    } catch (notificationError) {
      console.error('Failed to trigger in-app notification:', notificationError);
      // Don't fail the status update if notification fails
    }
    
    // Send email notifications (non-blocking)
    try {
      const candidateEmail = currentApp?.user_profiles?.email;
      const companyEmail = await getCompanyEmail(currentApp?.jobs?.companies?.id);
      const updaterEmail = updatedBy ? await getUserEmail(updatedBy) : 'System';
      
      const emailData = {
        applicationId: applicationId,
        jobTitle: currentApp?.jobs?.title,
        companyName: currentApp?.jobs?.companies?.name,
        companyEmail: companyEmail,
        candidateName: currentApp?.user_profiles?.full_name,
        candidateEmail: candidateEmail,
        oldStatus: oldStatus,
        newStatus: newStatus,
        notes: notes,
        updatedBy: updaterEmail,
        updatedAt: updatedApp?.updated_at
      };
      
      // Send status update emails asynchronously
      sendStatusUpdateNotifications(emailData)?.catch(emailError => {
        console.error('Failed to send status update emails:', emailError);
        // Don't fail the status update if emails fail
      });
    } catch (emailError) {
      console.error('Error preparing status update emails:', emailError);
      // Continue even if email sending fails
    }
    
    return {
      success: true,
      data: updatedApp,
      message: `Application status updated to ${newStatus}`
    };
    
  } catch (error) {
    console.error('Error updating application status:', error);
    return {
      success: false,
      error: error?.message
    };
  }
};

/**
 * Fetch pipeline stages for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of pipeline stages
 */
export const fetchPipelineStages = async (companyId) => {
  try {
    const { data, error } = await supabase
      ?.from('pipeline_stages')
      ?.select('*')
      ?.eq('company_id', companyId)
      ?.order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching pipeline stages:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch pipeline stages:', error);
    throw error;
  }
};

/**
 * Create a new pipeline stage
 * @param {string} companyId - The company ID
 * @param {string} name - Stage name
 * @param {string} color - Stage color class
 * @returns {Promise<Object>} Created stage
 */
export const createPipelineStage = async (companyId, name, color) => {
  try {
    // Get current max display order
    const { data: maxOrderData } = await supabase
      ?.from('pipeline_stages')
      ?.select('display_order')
      ?.eq('company_id', companyId)
      ?.order('display_order', { ascending: false })
      ?.limit(1);

    const newOrder = (maxOrderData?.[0]?.display_order || 0) + 1;

    const { data, error } = await supabase
      ?.from('pipeline_stages')
      ?.insert({
        company_id: companyId,
        name,
        color,
        display_order: newOrder,
        is_default: false
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Failed to create pipeline stage:', error);
    throw error;
  }
};

/**
 * Update application pipeline stage
 * @param {string} applicationId - The application ID
 * @param {string} stageId - The new pipeline stage ID
 * @returns {Promise<Object>} Updated application
 */
export const updateApplicationPipelineStage = async (applicationId, stageId) => {
  try {
    const { data, error } = await supabase
      ?.from('job_applications')
      ?.update({
        pipeline_stage_id: stageId,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', applicationId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Failed to update pipeline stage:', error);
    throw error;
  }
};

/**
 * Assign application to a manager
 * @param {string} applicationId - The application ID
 * @param {string} managerId - The manager user ID
 * @returns {Promise<Object>} Updated application
 */
export const assignApplicationToManager = async (applicationId, managerId) => {
  try {
    const { data, error } = await supabase
      ?.from('job_applications')
      ?.update({
        assigned_to: managerId,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', applicationId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Failed to assign application:', error);
    throw error;
  }
};

/**
 * Fetch notifications for a user
 * @param {string} userId - The user ID
 * @param {boolean} unreadOnly - Fetch only unread notifications
 * @returns {Promise<Array>} Array of notifications
 */
export const fetchUserNotifications = async (userId, unreadOnly = false) => {
  try {
    let query = supabase
      ?.from('application_notifications')
      ?.select(`
        *,
        job_applications (
          id,
          jobs (
            title
          ),
          user_profiles!job_applications_applicant_id_fkey (
            full_name
          )
        )
      `)
      ?.eq('recipient_id', userId)
      ?.order('created_at', { ascending: false });

    if (unreadOnly) {
      query = query?.eq('is_read', false);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Failed to fetch notifications:', error);
    throw error;
  }
};

/**
 * Mark notification as read
 * @param {string} notificationId - The notification ID
 * @returns {Promise<Object>} Updated notification
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const { data, error } = await supabase
      ?.from('application_notifications')
      ?.update({ is_read: true })
      ?.eq('id', notificationId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Failed to mark notification as read:', error);
    throw error;
  }
};

/**
 * Get application metrics for team management cards
 * @param {string} companyId - The company ID
 * @returns {Promise<Object>} Metrics including assigned applications per manager
 */
export const getApplicationMetricsForTeam = async (companyId) => {
  try {
    const { data, error } = await supabase
      ?.from('job_applications')
      ?.select(`
        id,
        status,
        assigned_to,
        pipeline_stage_id,
        applied_at,
        jobs!inner (
          company_id
        )
      `)
      ?.eq('jobs.company_id', companyId);

    if (error) throw error;

    // Calculate metrics per manager
    const managerMetrics = {};
    const stageMetrics = {};
    
    data?.forEach(app => {
      // Manager metrics
      if (app?.assigned_to) {
        if (!managerMetrics?.[app?.assigned_to]) {
          managerMetrics[app?.assigned_to] = {
            total: 0,
            pending: 0,
            inReview: 0,
            completed: 0
          };
        }
        managerMetrics[app?.assigned_to].total++;
        
        if (app?.status === 'pending') managerMetrics[app?.assigned_to].pending++;
        if (['reviewed', 'interview']?.includes(app?.status)) managerMetrics[app?.assigned_to].inReview++;
        if (['accepted', 'rejected']?.includes(app?.status)) managerMetrics[app?.assigned_to].completed++;
      }
      
      // Stage metrics
      if (app?.pipeline_stage_id) {
        stageMetrics[app?.pipeline_stage_id] = (stageMetrics?.[app?.pipeline_stage_id] || 0) + 1;
      }
    });

    return {
      managerMetrics,
      stageMetrics,
      totalApplications: data?.length || 0
    };
  } catch (error) {
    console.error('Failed to get application metrics:', error);
    throw error;
  }
};

/**
 * Get applications grouped by pipeline stage
 * @param {string} companyId - The company ID
 * @returns {Promise<Object>} Applications grouped by stage
 */
export const getApplicationsByPipelineStage = async (companyId) => {
  try {
    // Fetch pipeline stages
    const stages = await fetchPipelineStages(companyId);
    
    // Fetch all applications
    const { data: applications, error } = await supabase
      ?.from('job_applications')
      ?.select(`
        *,
        jobs!inner (
          company_id,
          title
        ),
        user_profiles!job_applications_applicant_id_fkey (
          full_name,
          email,
          avatar_url
        ),
        assigned_manager:user_profiles!assigned_to (
          full_name,
          email
        )
      `)
      ?.eq('jobs.company_id', companyId);

    if (error) throw error;

    // Group applications by stage
    const grouped = {};
    stages?.forEach(stage => {
      grouped[stage?.id] = {
        stage,
        applications: applications?.filter(app => app?.pipeline_stage_id === stage?.id) || []
      };
    });

    // Add unassigned applications
    grouped['unassigned'] = {
      stage: { id: 'unassigned', name: 'Unassigned', color: 'bg-gray-100 text-gray-800' },
      applications: applications?.filter(app => !app?.pipeline_stage_id) || []
    };

    return grouped;
  } catch (error) {
    console.error('Failed to get applications by stage:', error);
    throw error;
  }
};

/**
 * Get job view analytics
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Jobs with view counts and application conversion rates
 */
export const getJobViewAnalytics = async (companyId) => {
  try {
    const { data, error } = await supabase
      ?.from('jobs')
      ?.select(`
        id,
        title,
        view_count,
        posted_at,
        status,
        job_applications (
          id
        )
      `)
      ?.eq('company_id', companyId)
      ?.order('view_count', { ascending: false });

    if (error) throw error;

    // Calculate conversion rate
    return (data || [])?.map(job => ({
      id: job?.id,
      title: job?.title,
      viewCount: job?.view_count || 0,
      applicationCount: job?.job_applications?.length || 0,
      conversionRate: job?.view_count > 0 
        ? ((job?.job_applications?.length / job?.view_count) * 100)?.toFixed(2)
        : 0,
      postedAt: job?.posted_at,
      status: job?.status
    }));
  } catch (error) {
    console.error('Failed to get job view analytics:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time application updates for job seeker
 * @param {string} applicantId - The applicant user ID
 * @param {function} callback - Callback function to handle updates
 * @returns {function} Unsubscribe function
 */
export const subscribeToJobSeekerApplications = (applicantId, callback) => {
  const channel = supabase
    ?.channel('job-seeker-applications-changes')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'job_applications',
        filter: `applicant_id=eq.${applicantId}`
      },
      (payload) => {
        const eventType = payload?.eventType;
        const application = payload?.new || payload?.old;

        if (application) {
          callback({
            eventType,
            application: {
              id: application?.id,
              jobId: application?.job_id,
              applicantId: application?.applicant_id,
              resumeUrl: application?.resume_url,
              coverLetter: application?.cover_letter,
              status: application?.status,
              appliedAt: application?.applied_at,
              updatedAt: application?.updated_at
            }
          });
        }
      }
    )
    ?.subscribe();

  // Return unsubscribe function
  return () => {
    supabase?.removeChannel(channel);
  };
};

/**
 * Subscribe to real-time application updates for company dashboard
 * @param {string} companyId - The company ID
 * @param {function} callback - Callback function to handle updates
 * @returns {function} Unsubscribe function
 */
export const subscribeToCompanyApplications = (companyId, callback) => {
  const channel = supabase
    ?.channel('company-applications-changes')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'job_applications'
      },
      async (payload) => {
        const eventType = payload?.eventType;
        const application = payload?.new || payload?.old;

        if (application) {
          // Verify this application belongs to a job from this company
          const { data: job } = await supabase
            ?.from('jobs')
            ?.select('company_id')
            ?.eq('id', application?.job_id)
            ?.single();

          if (job?.company_id === companyId) {
            callback({
              eventType,
              application: {
                id: application?.id,
                jobId: application?.job_id,
                applicantId: application?.applicant_id,
                status: application?.status,
                appliedAt: application?.applied_at,
                updatedAt: application?.updated_at
              }
            });
          }
        }
      }
    )
    ?.subscribe();

  // Return unsubscribe function
  return () => {
    supabase?.removeChannel(channel);
  };
};

/**
 * Subscribe to application status history updates
 * @param {string} applicationId - The application ID
 * @param {function} callback - Callback function to handle updates
 * @returns {function} Unsubscribe function
 */
export const subscribeToApplicationStatusHistory = (applicationId, callback) => {
  const channel = supabase
    ?.channel('application-status-history-changes')
    ?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'application_status_history',
        filter: `application_id=eq.${applicationId}`
      },
      (payload) => {
        const historyEntry = payload?.new;

        if (historyEntry) {
          callback({
            id: historyEntry?.id,
            applicationId: historyEntry?.application_id,
            oldStatus: historyEntry?.old_status,
            newStatus: historyEntry?.new_status,
            changedBy: historyEntry?.changed_by,
            notes: historyEntry?.notes,
            createdAt: historyEntry?.created_at
          });
        }
      }
    )
    ?.subscribe();

  // Return unsubscribe function
  return () => {
    supabase?.removeChannel(channel);
  };
};

export default {
  fetchCompanyApplications,
  fetchApplicationDetails,
  getApplications,
  getApplicationById,
  updateApplicationStatus,
  updateApplicationStatusWithNotes,
  bulkUpdateApplicationStatus,
  getApplicationStatusHistory,
  getApplicationStatistics,
  getResumeDownloadUrl,
  getJobsWithApplicationCounts,
  updateApplicationStatusWithNotification,
  fetchJobSeekerApplications,
  getJobSeekerApplicationStatistics,
  fetchPipelineStages,
  createPipelineStage,
  updateApplicationPipelineStage,
  assignApplicationToManager,
  fetchUserNotifications,
  markNotificationAsRead,
  getApplicationMetricsForTeam,
  getApplicationsByPipelineStage,
  getJobViewAnalytics,
  subscribeToJobSeekerApplications,
  subscribeToCompanyApplications,
  subscribeToApplicationStatusHistory
};

// Invalidate cache on mutations
export const createApplication = async (applicationData) => {
  const result = await trackApiCall('createApplication', async () => {
    try {
      const { data, error } = await supabase?.from('job_applications')?.insert(applicationData)?.select(`
          *,
          job:jobs (
            id,
            title,
            company:company_profiles (
              id,
              company_name,
              logo_url
            )
          )
        `)?.single();

      if (error) throw error;

      // Invalidate related caches
      queryCache?.invalidateTable('job_applications');

      return { data, error: null };
    } catch (error) {
      console.error('Error creating application:', error);
      return { data: null, error };
    }
  });

  return result;
};

export const deleteApplication = async (applicationId) => {
  const result = await trackApiCall('deleteApplication', async () => {
    try {
      const { data, error } = await supabase?.from('job_applications')?.delete()?.eq('id', applicationId)?.select();

      if (error) throw error;

      // Invalidate related caches
      queryCache?.invalidateTable('job_applications');
      queryCache?.invalidateTable('application_status_history');

      return { data, error: null };
    } catch (error) {
      console.error('Error deleting application:', error);
      return { data: null, error };
    }
  });

  return result;
};

const increment_job_view_count = null;

export { increment_job_view_count };